import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { PopperMenu } from '../Popper';
import { Portal } from '../Portal';
import Button from '../presentation/Button/Button';
import { useZIndexMiddleware } from '../presentation/popper-modifiers';

type Props = {
  /** The menu's items (PopperMenuItem); call `onClose` after one is chosen. */
  children: (onClose: () => void) => ReactNode;
};

/** The "Actions" button of a dashboard card, with a menu that opens below it. */
export const DashboardActionsButton = ({ children }: Props) => {
  const [open, setOpen] = useState(false);

  const zIndexMiddleware = useZIndexMiddleware(8);

  const {
    refs: { setReference, setFloating },
    floatingStyles,
  } = useFloating({
    middleware: [zIndexMiddleware],
    placement: 'bottom-start',
  });

  const handleClose = useCallback(() => setOpen(false), []);
  const handleOpen = useCallback(() => setOpen(true), []);

  return (
    <>
      <div ref={setReference} onClick={handleOpen}>
        <Button.Secondary variant="brick">Actions</Button.Secondary>
      </div>
      {open && (
        <Portal>
          <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleClose} touchEvent="onTouchStart">
            <div ref={setFloating} style={floatingStyles}>
              <PopperMenu>{children(handleClose)}</PopperMenu>
            </div>
          </ClickAwayListener>
        </Portal>
      )}
    </>
  );
};
